import type { Address, Hash, Hex } from 'viem';
import { create } from 'zustand';

export type TxStage = 'wallet' | 'pending' | 'unknown' | 'confirmed' | 'failed' | 'cancelled';
export type TxOperation = 'buy' | 'sell' | 'claim';
/** Why a request failed, when the toast must say more than `transaction-failed`. */
export type TxFailureReason = 'insufficient-gas' | 'wrong-chain' | 'reverted';
export type TxRecord = {
  id: string;
  chainId: number;
  account: Address;
  operation: TxOperation;
  marketAddress: Address;
  step: 'approve' | 'execute';
  stage: TxStage;
  hash?: Hash;
  startedAt: number;
  approvalConfirmed?: boolean;
  replacementChanged?: boolean;
  failureReason?: TxFailureReason;
  /** Revert data the wallet returned for a send it refused to broadcast. In memory only. */
  revertData?: Hex;
};
export type TransactionState = {
  records: Record<string, TxRecord>;
  begin: (record: TxRecord) => boolean;
  update: (
    id: string,
    patch: Partial<
      Pick<TxRecord, 'stage' | 'hash' | 'step' | 'approvalConfirmed' | 'replacementChanged' | 'failureReason' | 'revertData'>
    >
  ) => void;
  findActive: (chainId: number, account: Address) => TxRecord | undefined;
  /** Newest dismissed finished-result start time per chain+account (see dismissKey). */
  dismissedThrough: Record<string, number>;
  /** Hides the finished result started at `startedAt` and every older one for the chain+account. */
  dismissThrough: (chainId: number, account: Address, startedAt: number) => void;
};
export const TRANSACTION_STORAGE_KEY = 'dpin-pending-transactions-v1';
export const dismissKey = (chainId: number, account: Address | string) => `${chainId}:${account.toLowerCase()}`;
export const isActiveTransaction = (record: TxRecord) => ['wallet', 'pending', 'unknown'].includes(record.stage);

const metadata = (record: TxRecord): TxRecord => ({
  id: record.id,
  chainId: record.chainId,
  account: record.account,
  operation: record.operation,
  marketAddress: record.marketAddress,
  step: record.step,
  stage: record.stage,
  hash: record.hash,
  startedAt: record.startedAt,
  approvalConfirmed: record.approvalConfirmed,
  replacementChanged: record.replacementChanged,
});

const restore = (): Record<string, TxRecord> => {
  try {
    const records: unknown = JSON.parse(sessionStorage.getItem(TRANSACTION_STORAGE_KEY) ?? '{}');
    if (!records || typeof records !== 'object') return {};
    return Object.fromEntries(
      Object.entries(records)
        .filter(([id, value]) => {
          const r = value as TxRecord;
          return (
            r &&
            r.id === id &&
            Number.isInteger(r.chainId) &&
            typeof r.account === 'string' &&
            /^0x[0-9a-f]{40}$/i.test(r.account) &&
            typeof r.marketAddress === 'string' &&
            /^0x[0-9a-f]{40}$/i.test(r.marketAddress) &&
            ['buy', 'sell', 'claim'].includes(r.operation) &&
            ['approve', 'execute'].includes(r.step) &&
            isActiveTransaction(r) &&
            Number.isFinite(r.startedAt) &&
            (!r.hash || /^0x[0-9a-f]{64}$/i.test(r.hash))
          );
        })
        .map(([id, r]) => [id, { ...metadata(r), stage: r.hash ? 'pending' : 'unknown' }])
    );
  } catch {
    return {};
  }
};

export const useTransactionStore = create<TransactionState>((set, get) => ({
  records: restore(),
  // In memory only: finished records are not restored on reload, so there is
  // nothing for a persisted dismissal to hide.
  dismissedThrough: {},
  dismissThrough: (chainId, account, startedAt) =>
    set(state => {
      const key = dismissKey(chainId, account);
      if ((state.dismissedThrough[key] ?? -Infinity) >= startedAt) return state;
      return { dismissedThrough: { ...state.dismissedThrough, [key]: startedAt } };
    }),
  begin: record => {
    if (get().records[record.id] || get().findActive(record.chainId, record.account)) return false;
    set(state => ({
      records: { ...state.records, [record.id]: { ...record, account: record.account.toLowerCase() as Address } },
    }));
    return true;
  },
  update: (id, patch) =>
    set(state => {
      const record = state.records[id];
      if (!record || !isActiveTransaction(record)) return state;
      return { records: { ...state.records, [id]: { ...record, ...patch } } };
    }),
  findActive: (chainId, account) =>
    Object.values(get().records).find(
      record =>
        record.chainId === chainId && record.account.toLowerCase() === account.toLowerCase() && isActiveTransaction(record)
    ),
}));

useTransactionStore.subscribe(state => {
  try {
    sessionStorage.setItem(
      TRANSACTION_STORAGE_KEY,
      JSON.stringify(
        Object.fromEntries(
          Object.entries(state.records)
            .filter(([, record]) => isActiveTransaction(record))
            .map(([id, record]) => [id, metadata(record)])
        )
      )
    );
  } catch {
    /* The in-memory lock remains authoritative if storage is unavailable. */
  }
});

const executing = new Set<string>();
export const startTransaction = (record: TxRecord): string | undefined => {
  const active = useTransactionStore.getState().findActive(record.chainId, record.account);
  if (active) {
    if (
      !active.approvalConfirmed ||
      active.hash ||
      active.operation !== record.operation ||
      active.marketAddress.toLowerCase() !== record.marketAddress.toLowerCase() ||
      executing.has(active.id)
    )
      return;
    executing.add(active.id);
    useTransactionStore.getState().update(active.id, { stage: 'wallet' });
    return active.id;
  }
  if (!useTransactionStore.getState().begin(record)) return;
  executing.add(record.id);
  return record.id;
};
export const finishTransactionRequest = (id: string) => {
  executing.delete(id);
};
export const canAcknowledgeWalletCancellation = (record: TxRecord) =>
  !record.hash && record.stage === 'unknown' && !record.approvalConfirmed && !executing.has(record.id);
export const canResumeTransaction = (record: TxRecord) =>
  !!record.approvalConfirmed && !record.hash && !executing.has(record.id);
export const failTransactionRequest = (id: string) => {
  const record = useTransactionStore.getState().records[id];
  if (record && !record.hash && record.stage === 'wallet') useTransactionStore.getState().update(id, { stage: 'failed' });
};

const GAS_FAILURE_MESSAGES = [/insufficient funds/i, /gas required exceeds allowance/i];
const WALLET_CHAIN_MISMATCH_NAMES = ['ChainMismatchError', 'ConnectorChainMismatchError'];
// How deep the walk goes into `cause`. A failure deeper than this stays unknown.
const MAX_CAUSE_DEPTH = 10;

/** The error and its causes, outermost first, up to MAX_CAUSE_DEPTH. Undefined when a level cannot be read. */
const causeChain = (error: unknown): Record<string, unknown>[] | undefined => {
  try {
    const chain: Record<string, unknown>[] = [];
    for (let cause = error; cause && typeof cause === 'object' && chain.length < MAX_CAUSE_DEPTH; ) {
      chain.push(cause as Record<string, unknown>);
      cause = (cause as Record<string, unknown>).cause;
    }
    return chain;
  } catch {
    return undefined;
  }
};

const isRevertHex = (value: unknown): value is Hex =>
  typeof value === 'string' && /^0x[0-9a-fA-F]+$/.test(value) && value.length >= 10;

/** Revert data the node or the wallet put on an error: `data`, `raw`, or `data.data` of any level. */
export const revertDataOf = (error: unknown): Hex | undefined => {
  for (const level of causeChain(error) ?? []) {
    try {
      for (const value of [level.data, level.raw]) {
        if (isRevertHex(value)) return value;
        const inner = value && typeof value === 'object' ? (value as { data?: unknown }).data : undefined;
        if (isRevertHex(inner)) return inner;
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export type SendFailure = 'rejected' | 'chain-mismatch' | 'revert' | 'insufficient-gas' | 'unknown';

/**
 * Class of a wallet send failure, in this order:
 * 1. rejected: the user refused (code 4001, code 5000 of WalletConnect, UserRejectedRequestError, any level);
 * 2. chain-mismatch: the wallet moved to another chain (ChainMismatchError, ConnectorChainMismatchError);
 * 3. revert: the wallet or node says the call reverts (revert data, code 3, "execution reverted" at the root, or
 *    viem's ContractFunctionRevertedError that carries revert data);
 * 4. insufficient-gas: InsufficientFundsError at any level, or the message of the ROOT cause says insufficient
 *    funds or gas allowance (a wrapper level that merely quotes the text does not count);
 * 5. unknown.
 * The order and the names are the same as classifyWalletError and classifyRevertData in the router SDK
 * (libs/sdk/contracts/core/apmm-router-errors.ts), where a revert wins over a wallet error. The SDK file is not in
 * this branch, so the rule is written here; when both are on one branch the SDK should import this function.
 * Pure: no store access.
 */
export const classifySendFailure = (error: unknown): SendFailure => {
  try {
    const chain = causeChain(error);
    if (!chain || chain.length === 0) return 'unknown';
    const root = chain[chain.length - 1];
    if (chain.some(e => e.name === 'UserRejectedRequestError' || e.code === 4001 || e.code === 5000)) return 'rejected';
    if (chain.some(e => WALLET_CHAIN_MISMATCH_NAMES.includes(String(e.name)))) return 'chain-mismatch';
    const rootText = [root.message, root.shortMessage, root.details].filter(v => typeof v === 'string') as string[];
    // viem wraps a wallet error with code 3 or -32603 into a new ContractFunctionRevertedError and drops the raw
    // provider error. `raw` (a string, "0x" for an empty revert) is only set when viem found revert data, so
    // that pair is a revert. The class name alone is not: a top-level -32603 "insufficient funds" gets the same
    // class and must still be read as a gas failure from its root text below.
    const wrappedRevert = chain.some(e => e.name === 'ContractFunctionRevertedError' && typeof e.raw === 'string');
    if (
      wrappedRevert ||
      revertDataOf(error) ||
      chain.some(e => e.code === 3) ||
      rootText.some(t => /execution reverted/i.test(t))
    ) {
      return 'revert';
    }
    if (
      chain.some(e => e.name === 'InsufficientFundsError') ||
      rootText.some(text => GAS_FAILURE_MESSAGES.some(pattern => pattern.test(text)))
    ) {
      return 'insufficient-gas';
    }
  } catch {
    // An unreadable error is ambiguous.
  }
  return 'unknown';
};

/**
 * True when a wallet send failed because the account cannot pay the gas, before anything was broadcast.
 * Same rule as classifySendFailure (the root cause decides, see there). Pure: no store access.
 */
export const isPreBroadcastGasFailure = (error: unknown): boolean => classifySendFailure(error) === 'insufficient-gas';

/**
 * An ambiguous wallet/RPC send failure may already have broadcast a transaction: it stays `unknown`. Only a
 * wallet refusal (`cancelled`) and a failure that certainly happened before the broadcast (`failed`: gas, wrong
 * chain, a revert the wallet found while it simulated) leave that path, and only while the record has no hash.
 */
export const sendTransaction = async (id: string, send: () => Promise<Hash>): Promise<Hash> => {
  // Approval permits reconfirmation only before execution reaches the wallet.
  // An ambiguous send response must never reopen this approved intent.
  useTransactionStore.getState().update(id, { approvalConfirmed: false });
  try {
    return await send();
  } catch (error) {
    const kind = classifySendFailure(error);
    const store = useTransactionStore.getState();
    if (kind === 'rejected') {
      store.update(id, { stage: 'cancelled' });
      throw error;
    }
    // As in failTransactionRequest: a record with a hash may be on chain.
    const record = store.records[id];
    if (record && !record.hash) {
      if (kind === 'insufficient-gas') {
        store.update(id, { stage: 'failed', failureReason: 'insufficient-gas' });
        throw new Error('transaction-insufficient-gas');
      }
      if (kind === 'chain-mismatch') {
        store.update(id, { stage: 'failed', failureReason: 'wrong-chain' });
        throw Object.assign(new Error('transaction-wrong-chain'), { cause: error });
      }
      if (kind === 'revert') {
        store.update(id, { stage: 'failed', failureReason: 'reverted', revertData: revertDataOf(error) });
        // The cause keeps the revert data for a caller that classifies it.
        throw Object.assign(new Error('transaction-not-sent'), { cause: error });
      }
    }
    store.update(id, { stage: 'unknown' });
    throw new Error('transaction-unknown');
  }
};
